import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { articlesAPI } from "../services/api";
import "./LessonsCarousel.css";

const MAX_CAROUSEL_LESSONS = 24;

const LessonsCarousel = () => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [isCarouselHidden, setIsCarouselHidden] = useState(() => {
    const saved = localStorage.getItem("lessonsCarouselHidden");
    return saved === "true";
  });
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const scrollRef = useRef(null);
  const scrollAmountRef = useRef(0);

  useEffect(() => {
    localStorage.setItem("lessonsCarouselHidden", isCarouselHidden);
  }, [isCarouselHidden]);

  useEffect(() => {
    const loadLessons = async () => {
      try {
        const [lessonsRes, notesSummaryRes, flashcardsSummaryRes] =
          await Promise.all([
          articlesAPI.getMyArticles("lesson", true),
          articlesAPI.getLessonLinksSummary("note"),
          articlesAPI.getLessonLinksSummary("flashcard"),
        ]);

        const allLessons = lessonsRes.data.articles
          .filter((a) => 
            a.contentType === "lesson" && 
            a.body !== "Folder placeholder" && 
            a.body !== "Subfolder placeholder"
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, MAX_CAROUSEL_LESSONS);

        // Mark lessons that have notes and flashcards
        const notesSummary = notesSummaryRes.data.summary || [];
        const flashcardsSummary = flashcardsSummaryRes.data.summary || [];
        const lessonIdsWithNotes = new Set(
          notesSummary.map((item) => String(item.lessonId)),
        );
        const lessonIdsWithFlashcards = new Set(
          flashcardsSummary.map((item) => String(item.lessonId)),
        );
        
        const lessonsWithMetadata = allLessons.map(lesson => ({
          ...lesson,
          hasNotes: lessonIdsWithNotes.has(String(lesson._id)),
          hasFlashcards: lessonIdsWithFlashcards.has(String(lesson._id))
        }));
        
        setLessons(lessonsWithMetadata);
      } catch {}
    };
    loadLessons();
  }, []);

  useEffect(() => {
    if (isCarouselHidden || lessons.length === 0) {
      return;
    }
    // Animation is now handled by CSS
  }, [lessons, isCarouselHidden]);

  if (lessons.length === 0) return null;

  return (
    <>
      {!isCarouselHidden && (
        <div className="lessons-carousel-container">
          <button
            className="lessons-carousel-toggle"
            onClick={() => setIsCarouselHidden(true)}
            title="Hide carousel"
          >
            <ChevronRight size={20} />
          </button>
          <div className="lessons-carousel-label">Latest Lessons</div>
          <div className="lessons-carousel">
            <div className="lessons-carousel-track">
              {lessons.map((lesson, index) => (
                <div
                  key={`${lesson._id}-${index}`}
                  className="lesson-carousel-card"
                  onClick={() => {
                    navigate(
                      `/view/lesson/${lesson._id}?folder=${encodeURIComponent(lesson.title)}`,
                    );
                  }}
                  onMouseEnter={() => setIsCarouselPaused(true)}
                  onMouseLeave={() => setIsCarouselPaused(false)}
                >
                  <div className="lesson-carousel-badges">
                    <div className="lesson-carousel-badge badge-lesson">L</div>
                    {lesson.hasNotes && <div className="lesson-carousel-badge badge-note">N</div>}
                    {lesson.hasFlashcards && <div className="lesson-carousel-badge badge-flashcard">F</div>}
                  </div>
                  <div className="lesson-carousel-name">
                    {lesson.chapter || `Lesson ${index + 1}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isCarouselHidden && (
        <button
          className="lessons-carousel-show-button"
          onClick={() => setIsCarouselHidden(false)}
          title="Show carousel"
        >
          <ChevronLeft size={20} />
        </button>
      )}
    </>
  );
};

export default LessonsCarousel;
